import { useMutation, useApolloClient } from '@apollo/client';
import {
  Button,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalMain,
  Paragraph,
  Stack,
  Textarea,
  useNotifications,
} from '@sixfold/common-ui';
import { isNil } from '@sixfold/typed-primitives';
import React, { useState, useCallback } from 'react';

import { createVehiclesMutation } from '../../company/graphql';
import {
  CreateVehiclesMutation as CreateVehiclesMutationType,
  CreateVehiclesMutationVariables,
  PAGINATION_PAGE_SIZE,
} from '../../lib/graphql';
import { managedVehiclesQuery } from '../../vehicle/graphql';
import { vehicleTrackerByTelematicsIntegrationAndVehicleDetailsQuery } from '../graphql';

type Props = {
  telematicsIntegrationId: string;
  trigger: React.ReactElement | string;
};

type VehicleInput = {
  licensePlate: string;
  remoteId: string;
};

type ValidationError = {
  lineNumber: number;
  type:
    | 'invalid_format'
    | 'invalid_license_plate'
    | 'invalid_remote_id'
    | 'duplicate_license_plate'
    | 'duplicate_remote_id'
    | 'existing_license_plate'
    | 'existing_remote_id';
  message: string;
};

type ValidationResult = {
  isValid: boolean;
  vehicles: VehicleInput[];
  errors: ValidationError[];
};

export const BulkAddVehiclesModal = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [vehicleListText, setVehicleListText] = useState('');
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [duplicateCheckResults, setDuplicateCheckResults] = useState<Map<string, { vehicleTrackerId: string } | null>>(
    new Map(),
  );
  const [validatedText, setValidatedText] = useState<string>('');

  const notify = useNotifications();
  const client = useApolloClient();
  const [createVehicles] = useMutation<CreateVehiclesMutationType, CreateVehiclesMutationVariables>(
    createVehiclesMutation,
  );

  const isInvalidLicensePlate = (licensePlate: string | undefined) => {
    return licensePlate !== undefined && licensePlate.trim() !== licensePlate;
  };

  const isInvalidRemoteId = (remoteId: string | undefined) => {
    return remoteId !== undefined && remoteId.trim() !== remoteId;
  };

  const isDuplicateTracker = useCallback((tracker: { vehicleTrackerId: string } | null) => {
    return tracker !== null;
  }, []);

  const parseVehicleList = useCallback(
    (text: string, resultsMap?: Map<string, { vehicleTrackerId: string } | null>): ValidationResult => {
      const lines = text.split('\n').filter((line) => line.trim() !== '');
      const vehicles: VehicleInput[] = [];
      const errors: ValidationError[] = [];
      const checkResults = resultsMap || duplicateCheckResults;

      lines.forEach((line, index) => {
        const trimmedLine = line.trim();
        if (trimmedLine === '') return;

        const lineNumber = index + 1;

        const parts = trimmedLine
          .split(',')
          .map((part) => part.trim())
          .filter((part) => part !== '');

        if (parts.length < 2) {
          errors.push({
            lineNumber,
            type: 'invalid_format',
            message: 'Each line must contain both license plate and remote ID separated by comma',
          });
          return;
        }

        if (parts.length > 2) {
          errors.push({
            lineNumber,
            type: 'invalid_format',
            message: 'Too many values. Expected format: license_plate, remote_id',
          });
          return;
        }

        const [licensePlate, remoteId] = parts;

        if (isNil(licensePlate) || licensePlate === '') {
          errors.push({
            lineNumber,
            type: 'invalid_license_plate',
            message: 'License plate cannot be empty',
          });
          return;
        }

        if (isInvalidLicensePlate(licensePlate)) {
          errors.push({
            lineNumber,
            type: 'invalid_license_plate',
            message: `License plate "${licensePlate}" has leading or trailing spaces`,
          });
          return;
        }

        if (isNil(remoteId) || remoteId === '') {
          errors.push({
            lineNumber,
            type: 'invalid_remote_id',
            message: 'Remote ID cannot be empty',
          });
          return;
        }

        if (isInvalidRemoteId(remoteId)) {
          errors.push({
            lineNumber,
            type: 'invalid_remote_id',
            message: `Remote ID "${remoteId}" has leading or trailing spaces`,
          });
          return;
        }

        const licensePlateTracker = checkResults.get(`license_${licensePlate}`);
        const remoteIdTracker = checkResults.get(`remote_${remoteId}`);

        if (licensePlateTracker && isDuplicateTracker(licensePlateTracker)) {
          errors.push({
            lineNumber,
            type: 'existing_license_plate',
            message: `License plate "${licensePlate}" is already used by another vehicle in this integration`,
          });
          return;
        }

        if (remoteIdTracker && isDuplicateTracker(remoteIdTracker)) {
          errors.push({
            lineNumber,
            type: 'existing_remote_id',
            message: `Remote ID "${remoteId}" is already used by another vehicle in this integration`,
          });
          return;
        }

        vehicles.push({ licensePlate, remoteId });
      });

      const licensePlates = vehicles.map((v) => v.licensePlate);
      const remoteIds = vehicles.map((v) => v.remoteId);

      const duplicateLicensePlates = licensePlates.filter((plate, index) => licensePlates.indexOf(plate) !== index);
      const duplicateRemoteIds = remoteIds.filter((id, index) => remoteIds.indexOf(id) !== index);

      if (duplicateLicensePlates.length > 0) {
        const uniqueDuplicates = Array.from(new Set(duplicateLicensePlates));
        errors.push({
          lineNumber: 0,
          type: 'duplicate_license_plate',
          message: `Duplicate license plates found: ${uniqueDuplicates.join(', ')}`,
        });
      }

      if (duplicateRemoteIds.length > 0) {
        const uniqueDuplicates = Array.from(new Set(duplicateRemoteIds));
        errors.push({
          lineNumber: 0,
          type: 'duplicate_remote_id',
          message: `Duplicate remote IDs found: ${uniqueDuplicates.join(', ')}`,
        });
      }

      return {
        isValid: errors.length === 0,
        vehicles,
        errors,
      };
    },
    [duplicateCheckResults, isDuplicateTracker],
  );

  const onToggle = () => {
    setVehicleListText('');
    setValidationResult(null);
    setIsValidating(false);
    setIsAdding(false);
    setDuplicateCheckResults(new Map());
    setValidatedText('');
    setIsOpen(!isOpen);
  };

  const handleValidate = async () => {
    if (!vehicleListText.trim()) {
      notify.error({ title: 'Please enter vehicle data' });
      return;
    }

    setIsValidating(true);
    setDuplicateCheckResults(new Map());
    setValidationResult(null);

    try {
      const lines = vehicleListText.split('\n').filter((line) => line.trim() !== '');
      const uniqueLicensePlates = new Set<string>();
      const uniqueRemoteIds = new Set<string>();

      lines.forEach((line) => {
        const parts = line
          .split(',')
          .map((part) => part.trim())
          .filter((part) => part !== '');
        if (parts.length === 2) {
          const [licensePlate, remoteId] = parts;
          if (licensePlate && !isInvalidLicensePlate(licensePlate)) {
            uniqueLicensePlates.add(licensePlate);
          }
          if (remoteId && !isInvalidRemoteId(remoteId)) {
            uniqueRemoteIds.add(remoteId);
          }
        }
      });

      const queryPromises: Promise<{ key: string; data: { vehicleTrackerId: string } | null }>[] = [];

      uniqueLicensePlates.forEach((licensePlate) => {
        queryPromises.push(
          client
            .query({
              query: vehicleTrackerByTelematicsIntegrationAndVehicleDetailsQuery,
              variables: {
                telematicsIntegrationId: props.telematicsIntegrationId,
                licensePlate,
              },
              fetchPolicy: 'network-only',
            })
            .then((result) => ({
              key: `license_${licensePlate}`,
              data: result.data.vehicleTrackerByTelematicsIntegrationAndVehicleDetails,
            })),
        );
      });

      uniqueRemoteIds.forEach((remoteId) => {
        queryPromises.push(
          client
            .query({
              query: vehicleTrackerByTelematicsIntegrationAndVehicleDetailsQuery,
              variables: {
                telematicsIntegrationId: props.telematicsIntegrationId,
                remoteId,
              },
              fetchPolicy: 'network-only',
            })
            .then((result) => ({
              key: `remote_${remoteId}`,
              data: result.data.vehicleTrackerByTelematicsIntegrationAndVehicleDetails,
            })),
        );
      });

      const results = await Promise.all(queryPromises);

      const newDuplicateCheckResults = new Map<string, { vehicleTrackerId: string } | null>();
      results.forEach(({ key, data }) => {
        newDuplicateCheckResults.set(key, data);
      });

      setDuplicateCheckResults(newDuplicateCheckResults);

      try {
        const result = parseVehicleList(vehicleListText, newDuplicateCheckResults);
        setValidationResult(result);
        setValidatedText(vehicleListText);

        if (result.isValid) {
          notify.success({
            title: 'Validation successful',
          });
        } else {
          notify.error({
            title: 'Validation failed',
          });
        }
      } catch (_validationError) {
        notify.error({ title: 'Validation error' });
      } finally {
        setIsValidating(false);
      }
    } catch (_error) {
      notify.error({ title: 'Failed to check for duplicates' });
      setIsValidating(false);
    }
  };

  const handleCancel = () => {
    onToggle();
  };

  const handleAdd = async () => {
    if (!validationResult || !validationResult.isValid) {
      notify.error({ title: 'Cannot add vehicles' });
      return;
    }

    setIsAdding(true);

    try {
      await createVehicles({
        variables: {
          input: {
            vehicles: validationResult.vehicles,
            telematicsIntegrationId: props.telematicsIntegrationId,
          },
        },
        refetchQueries: [
          {
            query: managedVehiclesQuery,
            variables: {
              integrationIds: [props.telematicsIntegrationId],
              limit: PAGINATION_PAGE_SIZE,
            },
          },
        ],
      });

      notify.success({
        title: 'Vehicles added successfully',
      });
      onToggle();
    } catch (_error) {
      notify.error({
        title: 'Failed to add vehicles',
      });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Modal trigger={props.trigger} open={isOpen} onToggle={onToggle}>
      <ModalHeader title="Add Multiple Vehicles" />
      <ModalMain>
        <Stack>
          <Paragraph>
            Enter vehicle data in the format: license_plate, remote_id (one vehicle per line). Use commas to separate
            license plate and remote ID on each line.
          </Paragraph>

          <Textarea
            label="Vehicle List"
            placeholder="ABC123, REMOTE001&#10;XYZ789, REMOTE002&#10;DEF456, REMOTE003"
            value={vehicleListText}
            onChange={(event) => setVehicleListText(event.value)}
            rows={8}
            name="vehicle_list"
          />

          {validationResult && (
            <Stack>
              <Paragraph>
                <strong>Validation Results:</strong>
              </Paragraph>

              {validationResult.isValid ? (
                <Paragraph>✓ Valid: {validationResult.vehicles.length} vehicle(s) ready to add</Paragraph>
              ) : (
                <Stack>
                  <Paragraph>✗ Validation failed with {validationResult.errors.length} error(s):</Paragraph>
                  {validationResult.errors.map((error, index) => (
                    <Paragraph key={index}>
                      {error.lineNumber > 0 ? `Line ${error.lineNumber}: ` : ''}• {error.message}
                    </Paragraph>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </ModalMain>
      <ModalFooter>
        <Stack justifyContent="end">
          <Button kind="subtle" onClick={handleCancel} disabled={isAdding}>
            Cancel
          </Button>
          <Button
            onClick={() => handleValidate()}
            kind="neutral"
            disabled={!vehicleListText.trim() || isValidating || isAdding}>
            {isValidating ? 'Validating...' : 'Validate'}
          </Button>
          <Button
            onClick={() => handleAdd()}
            kind="primary"
            disabled={!validationResult?.isValid || isAdding || vehicleListText !== validatedText}>
            {isAdding ? 'Adding...' : 'Add'}
          </Button>
        </Stack>
      </ModalFooter>
    </Modal>
  );
};
